import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  appendResult,
  combinePersonaInstruction,
  currentResult,
  EMPTY_RESULT_HISTORY,
  stepResult,
  type AiTextActionId,
  type StaticAiTextActionId,
} from '~/lib/ai/aiWriteState';
import { formatPromptAssistErrorMessage, promptAssistErrorStatus } from '~/lib/ai/promptAssist';
import { useLazyGetPersonaQuery, useListPersonasQuery } from '~/modules/query/ai/personas';
import type {
  PersonaListItem,
  PlacementUserInstructionAction,
  PlacementUserInstructionRequest,
} from '~/modules/query/ai/types';

import type { PlacementRunAi } from '~/components/pages/ai/placements/preview/TextField';

/** The fixed actions in the AI menu: Custom prompt first, then the edit-and-refine group. */
export const AI_TEXT_ACTIONS: Array<{ id: StaticAiTextActionId; label: string }> = [
  { id: 'custom-prompt', label: 'Custom prompt' },
  { id: 'rewrite', label: 'Rewrite' },
  { id: 'shorten', label: 'Shorten' },
  { id: 'expand', label: 'Expand' },
  { id: 'proofread', label: 'Proofread' },
];

const NO_TEXT_MESSAGE = 'Add text before running AI.';
const NO_HANDLER_MESSAGE = 'AI actions are unavailable until the placement is saved.';
const COPY_UNAVAILABLE_MESSAGE = 'Copy is unavailable here. Select the text and copy it manually.';

interface PendingTransformRequest {
  action: PlacementUserInstructionAction;
  inputText: string;
  userInstruction?: string;
}

export interface UseAiTextActionsOptions {
  aiDisabled: boolean;
  isDisabled: boolean;
  isRunning: boolean;
  onRunAi?: PlacementRunAi;
  /** Lets Replace and Add below write into the field. */
  onTextChange: (text: string) => void;
  text: string;
}

/**
 * The AI Write flow the Text Field and Text Area previews share: pick an action, review the
 * result in the popover (a history with a pager), then Replace or Add below. A request counter
 * drops the answer of a run that was stopped, retried or closed.
 */
export function useAiTextActions({
  aiDisabled,
  isDisabled,
  isRunning,
  onRunAi,
  onTextChange,
  text,
}: UseAiTextActionsOptions) {
  const [activeActionId, setActiveActionId] = useState<AiTextActionId | null>(null);
  const [title, setTitle] = useState('');
  const [personaInstruction, setPersonaInstruction] = useState<string | undefined>(undefined);
  const [isAiWriteOpen, setIsAiWriteOpen] = useState(false);
  const [history, setHistory] = useState(EMPTY_RESULT_HISTORY);
  const [isInstructionOpen, setIsInstructionOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const requestRef = useRef(0);
  /** What Try again replays: set by every path that can show an error, so a retry repeats what failed. */
  const lastRetryRef = useRef<(() => void) | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: personasData } = useListPersonasQuery();
  const [getPersona] = useLazyGetPersonaQuery();

  const activePersonas = useMemo(
    () =>
      (personasData?.data ?? [])
        .filter((persona) => persona.status === 'active')
        .slice()
        .sort((left, right) => left.name.localeCompare(right.name)),
    [personasData],
  );

  const modalText = currentResult(history);
  const hasGeneratedResult = history.items.length > 0;
  const isBusy = isRunning || isGenerating;

  const runTransform = useCallback(
    async (request: PendingTransformRequest) => {
      lastRetryRef.current = () => {
        void runTransform(request);
      };
      const inputText = request.inputText.trim();

      if (!inputText) {
        setErrorMessage(NO_TEXT_MESSAGE);
        return;
      }

      if (!onRunAi) {
        setErrorMessage(NO_HANDLER_MESSAGE);
        return;
      }

      requestRef.current += 1;
      const requestId = requestRef.current;
      setErrorMessage(null);
      setIsGenerating(true);

      try {
        const response = await onRunAi({
          action: request.action,
          inputText,
          outputFormat: 'plain',
          userInstruction: request.userInstruction,
        } satisfies PlacementUserInstructionRequest);
        const output = response?.outputText.trim();

        if (!output) {
          throw new Error('The AI returned an empty response.');
        }

        if (requestRef.current === requestId) {
          setHistory((previous) => appendResult(previous, output));
        }
      } catch (error) {
        if (requestRef.current === requestId) {
          setErrorMessage(
            formatPromptAssistErrorMessage({
              message: error instanceof Error ? error.message : '',
              status: promptAssistErrorStatus(error),
            }),
          );
        }
      } finally {
        if (requestRef.current === requestId) {
          setIsGenerating(false);
        }
      }
    },
    [onRunAi],
  );

  const resetSession = () => {
    setHistory(EMPTY_RESULT_HISTORY);
    setErrorMessage(null);
    setIsCopied(false);
    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = null;
    }
  };

  const openAction = (actionId: StaticAiTextActionId) => {
    if (isDisabled || aiDisabled || isBusy || !onRunAi) {
      return;
    }

    const isCustomPrompt = actionId === 'custom-prompt';

    setActiveActionId(actionId);
    setPersonaInstruction(undefined);
    setTitle(AI_TEXT_ACTIONS.find((entry) => entry.id === actionId)?.label ?? '');
    resetSession();
    setInstruction('');
    setIsInstructionOpen(isCustomPrompt);
    setIsAiWriteOpen(true);

    if (!isCustomPrompt) {
      void runTransform({ action: actionId, inputText: text });
    }
  };

  const openPersona = (persona: PersonaListItem) => {
    if (isDisabled || aiDisabled || isBusy || !onRunAi) {
      return;
    }

    setActiveActionId('persona');
    setPersonaInstruction(undefined);
    setTitle(persona.name);
    resetSession();
    setInstruction('');
    setIsInstructionOpen(false);
    setIsAiWriteOpen(true);

    if (!text.trim()) {
      void runTransform({ action: 'custom-prompt', inputText: text });
      return;
    }

    requestRef.current += 1;
    const requestId = requestRef.current;
    setIsGenerating(true);

    getPersona(persona.id)
      .unwrap()
      .then((response) => {
        if (requestRef.current !== requestId) {
          return undefined;
        }

        const instructions = response.data.instructions;
        setPersonaInstruction(instructions);
        return runTransform({
          action: 'custom-prompt',
          inputText: text,
          userInstruction: instructions,
        });
      })
      .catch(() => {
        if (requestRef.current !== requestId) {
          return;
        }

        setIsGenerating(false);
        // Retrying a failed persona load fetches the persona again, not a plain run without it.
        lastRetryRef.current = () => openPersona(persona);
        setErrorMessage('The persona could not be loaded. Try again.');
      });
  };

  const close = () => {
    requestRef.current += 1;
    setIsAiWriteOpen(false);
    setActiveActionId(null);
    setTitle('');
    setPersonaInstruction(undefined);
    setInstruction('');
    setIsInstructionOpen(false);
    setIsGenerating(false);
    resetSession();
  };

  const stop = () => {
    requestRef.current += 1;
    setIsGenerating(false);
  };

  const retry = () => lastRetryRef.current?.();

  const copy = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      toast.error(COPY_UNAVAILABLE_MESSAGE);
      return;
    }

    navigator.clipboard
      .writeText(modalText)
      .then(() => {
        setIsCopied(true);
        if (copiedTimeoutRef.current) {
          clearTimeout(copiedTimeoutRef.current);
        }
        copiedTimeoutRef.current = setTimeout(() => {
          setIsCopied(false);
          copiedTimeoutRef.current = null;
        }, 1500);
      })
      .catch(() => toast.error(COPY_UNAVAILABLE_MESSAGE));
  };

  const previousResult = () => setHistory((previous) => stepResult(previous, -1));
  const nextResult = () => setHistory((previous) => stepResult(previous, 1));

  const moreLikeThis = () => {
    if (!activeActionId) {
      return;
    }

    void runTransform({
      action: activeActionId === 'persona' ? 'custom-prompt' : activeActionId,
      inputText: modalText.trim() ? modalText : text,
      userInstruction: activeActionId === 'persona' ? personaInstruction : undefined,
    });
  };

  const applyInstruction = () => {
    const typed = instruction.trim();

    if (!typed) {
      return;
    }

    setInstruction('');
    setIsInstructionOpen(false);

    // A persona's instructions stay in front of the typed refinement instead of being dropped.
    void runTransform({
      action: 'custom-prompt',
      inputText: modalText.trim() ? modalText : text,
      userInstruction:
        activeActionId === 'persona' && personaInstruction
          ? combinePersonaInstruction(personaInstruction, typed)
          : typed,
    });
  };

  const closeInstruction = () => {
    setInstruction('');
    setIsInstructionOpen(false);
  };

  const replace = () => {
    onTextChange(modalText);
    close();
  };

  const addBelow = () => {
    onTextChange(text.trim() ? `${text}\n${modalText}` : modalText);
    close();
  };

  return {
    activeActionId,
    activePersonas,
    addBelow,
    applyInstruction,
    close,
    closeInstruction,
    copy,
    errorMessage,
    hasGeneratedResult,
    instruction,
    isAiWriteOpen,
    isBusy,
    isCopied,
    isGenerating,
    isInstructionOpen,
    modalText,
    moreLikeThis,
    nextResult,
    openAction,
    openInstruction: () => setIsInstructionOpen(true),
    openPersona,
    previousResult,
    replace,
    resultCount: history.items.length,
    resultIndex: history.index,
    retry,
    setInstruction,
    stop,
    title,
  } as const;
}

export type AiTextActions = ReturnType<typeof useAiTextActions>;
